import React from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import CurrencyInput from '../../../../molecules/CurrencyInput';
import TextEdit from '../../../../molecules/TextEdit';
import { FaTimes, FaPlus, FaFilePdf } from 'react-icons/fa';
import { ContractGuaranteeItem } from '../../types/operations';

export interface ContractGuaranteeData {
    items: ContractGuaranteeItem[];
    files: Map<string, File>;
    existingFiles: Map<string, string>;
}

interface ContractGuaranteeManagerProps {
    value: ContractGuaranteeData;
    onChange: (data: ContractGuaranteeData) => void;
    onFileDownload?: (fileName: string) => void;
    disabled?: boolean;
    errorFields?: Array<{ fieldName: string; message: string }>;
}

const MIN_ROWS = 5;

const generateId = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const formatCurrency = (val: number): string =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ContractGuaranteeManager: React.FC<ContractGuaranteeManagerProps> = ({
    value,
    onChange,
    onFileDownload,
    disabled,
}) => {
    const newContractValue = useSignal(0);
    const newAnticipatedValue = useSignal(0);
    const newContractNumber = useSignal('');
    const newDescription = useSignal('');
    const newFile = useSignal<File | null>(null);

    const handleAdd = () => {
        if (newContractValue.value <= 0) {
            toast.error('Informe o valor do contrato');
            return;
        }

        const id = generateId();
        const newItem: ContractGuaranteeItem = {
            collateral_id: id,
            contract_number: newContractNumber.value || '',
            contract_value: newContractValue.value,
            anticipated_value: newAnticipatedValue.value || 0,
            file_name: newFile.value?.name || '',
            description: newDescription.value || '',
        };

        const newFiles = new Map(value.files);
        if (newFile.value) {
            newFiles.set(id, newFile.value);
        }

        onChange({
            items: [...value.items, newItem],
            files: newFiles,
            existingFiles: value.existingFiles,
        });

        newContractValue.value = 0;
        newAnticipatedValue.value = 0;
        newContractNumber.value = '';
        newDescription.value = '';
        newFile.value = null;
    };

    const handleRemove = (index: number) => {
        const item = value.items[index];
        const updatedFiles = new Map(value.files);
        const updatedExisting = new Map(value.existingFiles);
        updatedFiles.delete(item.collateral_id);
        updatedExisting.delete(item.collateral_id);

        onChange({
            items: value.items.filter((_, i) => i !== index),
            files: updatedFiles,
            existingFiles: updatedExisting,
        });
    };

    const handleFileChangeForItem = (collateralId: string, file: File | null) => {
        const updatedFiles = new Map(value.files);
        const updatedExisting = new Map(value.existingFiles);

        if (file) {
            updatedFiles.set(collateralId, file);
            updatedExisting.delete(collateralId);
        } else {
            updatedFiles.delete(collateralId);
        }

        const updatedItems = value.items.map(item =>
            item.collateral_id === collateralId
                ? { ...item, file_name: file?.name || '' }
                : item
        );

        onChange({ items: updatedItems, files: updatedFiles, existingFiles: updatedExisting });
    };

    const handleRemoveExistingFile = (collateralId: string) => {
        const updatedExisting = new Map(value.existingFiles);
        updatedExisting.delete(collateralId);

        const updatedItems = value.items.map(item =>
            item.collateral_id === collateralId
                ? { ...item, file_name: '' }
                : item
        );

        onChange({ items: updatedItems, files: value.files, existingFiles: updatedExisting });
    };

    const emptyRowCount = Math.max(0, MIN_ROWS - value.items.length);

    return (
        <div className="space-y-4">
            {/* Add form */}
            <div className="grid grid-cols-12 gap-3 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <div className="col-span-2">
                    <TextEdit
                        id="cg_contract_number"
                        label="Nº Contrato"
                        value={newContractNumber.value}
                        onChange={(e) => { newContractNumber.value = (e.target as HTMLInputElement).value; }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-2">
                    <CurrencyInput
                        id="cg_contract_value"
                        label="Valor Contrato *"
                        prefix="R$"
                        fractionDigits={2}
                        value={newContractValue.value}
                        onChange={(v) => { newContractValue.value = v; }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-2">
                    <CurrencyInput
                        id="cg_anticipated_value"
                        label="Valor Antecipado"
                        prefix="R$"
                        fractionDigits={2}
                        value={newAnticipatedValue.value}
                        onChange={(v) => { newAnticipatedValue.value = v; }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-2">
                    <TextEdit
                        id="cg_description"
                        label="Descrição"
                        value={newDescription.value}
                        onChange={(e) => { newDescription.value = (e.target as HTMLInputElement).value; }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contrato (PDF)
                    </label>
                    {newFile.value ? (
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-2 py-1.5 rounded border border-green-200 dark:border-green-700">
                            <FaFilePdf size={14} />
                            <span className="truncate flex-1">{newFile.value.name}</span>
                            <button type="button" onClick={() => { newFile.value = null; }} className="text-red-500 hover:text-red-700">
                                <FaTimes size={12} />
                            </button>
                        </div>
                    ) : (
                        <label
                            htmlFor="cg-new-file-upload"
                            className="flex items-center justify-center h-9 border-2 border-dashed border-gray-300 dark:border-gray-500 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                            <span className="text-xs text-gray-500 dark:text-gray-400">Selecionar PDF</span>
                            <input
                                id="cg-new-file-upload"
                                type="file"
                                className="hidden"
                                accept=".pdf,application/pdf"
                                disabled={disabled}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) newFile.value = file;
                                    e.target.value = '';
                                }}
                            />
                        </label>
                    )}
                </div>
                <div className="col-span-2 flex items-end">
                    <button type="button"
                        onClick={handleAdd}
                        disabled={disabled}
                        className="flex items-center gap-1 px-3 py-2 mb-4 bg-gray-600 dark:bg-gray-500 text-white text-sm rounded hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FaPlus size={12} /> Adicionar
                    </button>
                </div>
            </div>

            {/* Table with min 5 rows + zebra */}
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                    <thead className="text-xs uppercase bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                        <tr>
                            <th className="px-3 py-2">Nº Contrato</th>
                            <th className="px-3 py-2">Valor Contrato</th>
                            <th className="px-3 py-2">Valor Antecipado</th>
                            <th className="px-3 py-2">Descrição</th>
                            <th className="px-3 py-2">Arquivo</th>
                            <th className="px-3 py-2 w-16">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {value.items.map((item, index) => {
                            const existingFile = value.existingFiles.get(item.collateral_id);
                            const uploadedFile = value.files.get(item.collateral_id);
                            const fileName = uploadedFile?.name || existingFile || item.file_name || '';

                            return (
                                <tr key={item.collateral_id} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                    <td className="px-3 py-2">{item.contract_number || '-'}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.contract_value)}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.anticipated_value || 0)}</td>
                                    <td className="px-3 py-2">{item.description || '-'}</td>
                                    <td className="px-3 py-2">
                                        {fileName ? (
                                            <div className="flex items-center gap-2">
                                                <FaFilePdf className="text-red-500" size={12} />
                                                {existingFile && !uploadedFile ? (
                                                    <button type="button"
                                                        onClick={() => onFileDownload?.(existingFile)}
                                                        className="text-blue-600 dark:text-blue-400 underline text-xs hover:text-blue-800 bg-transparent border-0 p-0"
                                                    >
                                                        {existingFile}
                                                    </button>
                                                ) : (
                                                    <span className="text-xs truncate max-w-[120px]">{fileName}</span>
                                                )}
                                                {!disabled && (
                                                    <button type="button"
                                                        onClick={() => existingFile ? handleRemoveExistingFile(item.collateral_id) : handleFileChangeForItem(item.collateral_id, null)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <FaTimes size={10} />
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <label htmlFor={`cg-file-${item.collateral_id}`} className="text-xs text-blue-600 dark:text-blue-400 underline cursor-pointer hover:text-blue-800">
                                                Upload PDF
                                                <input
                                                    id={`cg-file-${item.collateral_id}`}
                                                    type="file"
                                                    className="hidden"
                                                    accept=".pdf,application/pdf"
                                                    disabled={disabled}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleFileChangeForItem(item.collateral_id, file);
                                                        e.target.value = '';
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <button type="button" onClick={() => handleRemove(index)} disabled={disabled} className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50" title="Remover">
                                            <FaTimes size={14} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`empty-${i}`} className={`${(value.items.length + i) % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ContractGuaranteeManager;
